import { captureException, captureMessage } from "@sentry/react";
import { TFunction } from "i18next";
import parsePhoneNumber from "libphonenumber-js";
import { toast } from "sonner";

import { IPhoneNumber } from "@/types/phone.types";

export const selectOutboundPhoneNumberAutomatically = ({
    calledNumber,
    phoneNumbers,
    selectedBrand,
    setIsBrandSelectorOpen,
    t,
}: {
    calledNumber: string;
    phoneNumbers: IPhoneNumber[];
    selectedBrand: string;
    setIsBrandSelectorOpen: (isOpen: boolean) => void;
    t: TFunction;
}) => {
    if (!phoneNumbers?.length) {
        toast.error(t("no_phone_numbers_found"));
        throw new Error("[Auto number] No phone numbers found");
    }

    const enabledPhoneNumbers = phoneNumbers.filter((phoneNumber) => phoneNumber.isActive);

    if (!selectedBrand) {
        setIsBrandSelectorOpen(true);
        toast.error(t("no_brand_selected"));
        console.error("[Auto number] No brand selected");
        captureException(new Error("[Auto number] No brand selected"));
        return;
    }

    // First rule: filter by brand and sort by priority
    const brandNumbers = enabledPhoneNumbers
        .filter((phoneNumber) => phoneNumber.brands?.includes(selectedBrand))
        .sort((a, b) => (b.priority || 0) - (a.priority || 0));
    if (!brandNumbers.length) {
        toast.error(t("no_phone_numbers_found_for_brand"));
        console.error("[Auto number] No phone numbers found for brand");
        captureMessage("[Auto number] No phone numbers found for brand");
        return;
    }

    const fallbackNumber = brandNumbers[0].number;

    // Second rule: find one number by country
    try {
        console.log("[Auto number] Called number", calledNumber);

        const parsedCalledPhoneNumber = parsePhoneNumber(calledNumber);
        console.log("[Auto number] Parsed called number", parsedCalledPhoneNumber?.country);

        if (!parsedCalledPhoneNumber?.country) {
            return fallbackNumber; // Fallback to first number if can't parse
        }

        // Find a phone number with matching country code by assigned countries
        const matchingNumberByCountry = [...brandNumbers]
            .sort((a, b) => (a.countries?.length || 0) - (b.countries?.length || 0)) // Sort by which one has fewer countries
            .find((phoneNumber) => {
                return phoneNumber.countries?.includes(parsedCalledPhoneNumber.country!);
            });
        if (matchingNumberByCountry) {
            console.log(`[Auto number] Matching number by country`, matchingNumberByCountry);
            return matchingNumberByCountry.number;
        }

        // Find a phone number with matching country code by country code
        const matchingNumberByCountryCode = brandNumbers.find((phoneNumber) => {
            const parsedPhoneNumber = parsePhoneNumber(phoneNumber.number);
            return parsedPhoneNumber?.country === parsedCalledPhoneNumber.country;
        });
        if (matchingNumberByCountryCode) {
            console.log(`[Auto number] Matching number by country code`, matchingNumberByCountryCode);
            return matchingNumberByCountryCode.number;
        }

        // TODO: Third rule: Retry rules (recognize waht number we used previously and try with another one if the calls were not successful)

        return fallbackNumber;
    } catch (error) {
        console.error("[Auto number] Error parsing phone number:", error);
        return fallbackNumber; // Fallback to first number on error
    }
};
